import { useEffect, useState } from 'react';
import { api } from '../api/client.ts';

interface CsbLigne {
  id: string;
  nom: string;
  code: string | null;
  actif: boolean;
  plan: 'RCE' | 'RCE_CPN' | 'RCE_CPN_VACCINATION' | null;
  abonnement_statut: string | null;
  date_fin: string | null;
  date_fin_essai: string | null;
  prix_mensuel: string | null;
}

interface UtilisateurLigne {
  id: string;
  nom: string;
  email: string;
  role: 'RESPONSABLE_CSB' | 'AGENT_SAISIE' | 'ADMIN';
  actif: boolean;
  derniere_connexion: string | null;
}

const PLANS: Array<{ valeur: 'RCE' | 'RCE_CPN' | 'RCE_CPN_VACCINATION'; libelle: string; prix: number }> = [
  { valeur: 'RCE', libelle: 'Saisie RCE', prix: 3000 },
  { valeur: 'RCE_CPN', libelle: 'Saisie RCE + CPN', prix: 5000 },
  { valeur: 'RCE_CPN_VACCINATION', libelle: 'Saisie RCE + CPN + Vaccination', prix: 7000 },
];

export default function AdminComptes() {
  const [csbListe, setCsbListe] = useState<CsbLigne[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [csbOuvert, setCsbOuvert] = useState<string | null>(null);
  const [utilisateurs, setUtilisateurs] = useState<Record<string, UtilisateurLigne[]>>({});

  const [formOuvert, setFormOuvert] = useState(false);
  const [nouveauNom, setNouveauNom] = useState('');
  const [nouveauCode, setNouveauCode] = useState('');
  const [nouveauPlan, setNouveauPlan] = useState<'RCE' | 'RCE_CPN' | 'RCE_CPN_VACCINATION'>('RCE');
  const [responsableNom, setResponsableNom] = useState('');
  const [responsableEmail, setResponsableEmail] = useState('');
  const [motDePasseManuel, setMotDePasseManuel] = useState('');
  const [creationEnCours, setCreationEnCours] = useState(false);
  const [dernierMotDePasse, setDernierMotDePasse] = useState<{ email: string; mdp: string } | null>(null);

  useEffect(() => {
    charger();
  }, []);

  async function charger() {
    setChargement(true);
    setErreur(null);
    try {
      const data = await api.get<CsbLigne[]>('/admin/csb');
      setCsbListe(data);
    } catch (err: any) {
      setErreur(err.message ?? 'Impossible de charger les CSB');
    } finally {
      setChargement(false);
    }
  }

  async function chargerUtilisateurs(csbId: string) {
    try {
      const data = await api.get<UtilisateurLigne[]>(`/admin/csb/${csbId}/utilisateurs`);
      setUtilisateurs((prev) => ({ ...prev, [csbId]: data }));
    } catch (err: any) {
      setErreur(err.message ?? 'Impossible de charger les comptes de ce CSB');
    }
  }

  function toggleCsb(csbId: string) {
    if (csbOuvert === csbId) {
      setCsbOuvert(null);
    } else {
      setCsbOuvert(csbId);
      if (!utilisateurs[csbId]) chargerUtilisateurs(csbId);
    }
  }

  async function creerCsb() {
    if (nouveauNom.trim().length < 2 || responsableNom.trim().length < 2 || !responsableEmail.includes('@')) return;
    setCreationEnCours(true);
    setErreur(null);
    setDernierMotDePasse(null);
    try {
      const resultat = await api.post<{ resultats: Array<{ ok: boolean; email?: string; motDePasseTemporaire?: string; erreur?: string }> }>(
        '/admin/csb/lot',
        {
          comptes: [
            {
              csbNom: nouveauNom.trim(),
              csbCode: nouveauCode.trim() || undefined,
              responsableNom: responsableNom.trim(),
              email: responsableEmail.trim(),
              plan: nouveauPlan,
              ...(motDePasseManuel.trim().length >= 8 ? { password: motDePasseManuel.trim() } : {}),
            },
          ],
        }
      );
      const r = resultat.resultats[0];
      if (!r.ok) {
        setErreur(r.erreur ?? 'Erreur lors de la création');
      } else {
        if (r.motDePasseTemporaire) setDernierMotDePasse({ email: r.email!, mdp: r.motDePasseTemporaire });
        setNouveauNom(''); setNouveauCode(''); setResponsableNom(''); setResponsableEmail(''); setMotDePasseManuel('');
        setNouveauPlan('RCE');
        setFormOuvert(false);
        await charger();
      }
    } catch (err: any) {
      setErreur(err.message ?? 'Erreur lors de la création');
    } finally {
      setCreationEnCours(false);
    }
  }

  async function toggleCsbActif(csb: CsbLigne) {
    try {
      await api.patch(`/admin/csb/${csb.id}`, { actif: !csb.actif });
      await charger();
    } catch (err: any) {
      setErreur(err.message ?? 'Erreur');
    }
  }

  async function changerPlan(csbId: string, plan: string) {
    try {
      await api.patch(`/admin/csb/${csbId}/plan`, { plan });
      await charger();
    } catch (err: any) {
      setErreur(err.message ?? 'Erreur');
    }
  }

  async function toggleUtilisateurActif(csbId: string, u: UtilisateurLigne) {
    try {
      await api.post(`/admin/users/${u.id}/${u.actif ? 'desactiver' : 'reactiver'}`);
      await chargerUtilisateurs(csbId);
    } catch (err: any) {
      setErreur(err.message ?? 'Erreur');
    }
  }

  async function reinitialiserMotDePasse(csbId: string, u: UtilisateurLigne) {
    try {
      const r = await api.post<{ motDePasseTemporaire: string }>(`/admin/users/${u.id}/reinitialiser`);
      setDernierMotDePasse({ email: u.email, mdp: r.motDePasseTemporaire });
    } catch (err: any) {
      setErreur(err.message ?? 'Erreur');
    }
  }

  return (
    <div>
      <h1>Administration — Comptes CSB</h1>

      {erreur && <p className="messages-erreur">{erreur}</p>}

      {dernierMotDePasse && (
        <div style={{ background: '#fff8e6', border: '1px solid #e0c674', borderRadius: 10, padding: 12, marginBottom: 16 }}>
          <strong>Mot de passe pour {dernierMotDePasse.email} :</strong> <code>{dernierMotDePasse.mdp}</code>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>Note-le maintenant — il ne sera plus jamais affiché.</p>
        </div>
      )}

      <button onClick={() => setFormOuvert(!formOuvert)}>{formOuvert ? 'Annuler' : '+ Nouveau CSB'}</button>

      {formOuvert && (
        <div style={{ background: 'white', border: '1px solid #dcdcdc', borderRadius: 12, padding: 16, marginTop: 12 }}>
          <label htmlFor="nom-csb">Nom du CSB *</label>
          <input id="nom-csb" value={nouveauNom} onChange={(e) => setNouveauNom(e.target.value)} />

          <label htmlFor="code-csb">Code CSB (optionnel)</label>
          <input id="code-csb" value={nouveauCode} onChange={(e) => setNouveauCode(e.target.value)} />

          <label>Palier d'abonnement</label>
          <div className="chip-grid">
            {PLANS.map((p) => (
              <div key={p.valeur} className={'chip' + (nouveauPlan === p.valeur ? ' selected' : '')} onClick={() => setNouveauPlan(p.valeur)}>
                {p.libelle} — {p.prix} Ar
              </div>
            ))}
          </div>

          <label htmlFor="resp-nom">Nom du responsable *</label>
          <input id="resp-nom" value={responsableNom} onChange={(e) => setResponsableNom(e.target.value)} />

          <label htmlFor="resp-email">E-mail du responsable *</label>
          <input id="resp-email" type="email" value={responsableEmail} onChange={(e) => setResponsableEmail(e.target.value)} />

          <label htmlFor="resp-mdp">Mot de passe (laisser vide pour en générer un automatiquement)</label>
          <input id="resp-mdp" value={motDePasseManuel} onChange={(e) => setMotDePasseManuel(e.target.value)} placeholder="min. 8 caractères" />

          <div style={{ marginTop: 12 }}>
            <button onClick={creerCsb} disabled={creationEnCours}>{creationEnCours ? 'Création…' : 'Créer'}</button>
          </div>
        </div>
      )}

      {chargement && <p style={{ marginTop: 16 }}>Chargement…</p>}

      {!chargement && csbListe.map((csb) => (
        <div key={csb.id} style={{ background: 'white', border: '1px solid #dcdcdc', borderRadius: 12, padding: 14, marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => toggleCsb(csb.id)}>
            <div>
              <strong>{csb.nom}</strong> {csb.code && <span style={{ color: '#666' }}>({csb.code})</span>}
              <div style={{ fontSize: '0.85rem', color: csb.actif ? '#0369a1' : '#b3261e' }}>
                {csb.actif ? 'Actif' : 'Désactivé'} · {csb.plan ?? '—'} · {csb.abonnement_statut ?? 'Aucun abonnement'}
                {csb.prix_mensuel && ` · ${csb.prix_mensuel} Ar/mois`}
              </div>
            </div>
            <span>{csbOuvert === csb.id ? '▲' : '▼'}</span>
          </div>

          {csbOuvert === csb.id && (
            <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 12 }}>
              <label>Changer de palier</label>
              <div className="chip-grid" style={{ marginBottom: 12 }}>
                {PLANS.map((p) => (
                  <div key={p.valeur} className={'chip' + (csb.plan === p.valeur ? ' selected' : '')} onClick={() => changerPlan(csb.id, p.valeur)}>
                    {p.libelle} — {p.prix} Ar
                  </div>
                ))}
              </div>

              <button className={csb.actif ? 'danger' : ''} onClick={() => toggleCsbActif(csb)}>
                {csb.actif ? 'Désactiver ce CSB (coupe tous ses comptes)' : 'Activer ce CSB'}
              </button>

              <h3 style={{ marginTop: 16 }}>Comptes</h3>
              {!utilisateurs[csb.id] && <p>Chargement des comptes…</p>}
              {utilisateurs[csb.id]?.map((u) => (
                <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                  <div>
                    <div>{u.nom} <span style={{ color: '#666', fontSize: '0.85rem' }}>({u.role})</span></div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{u.email} — {u.actif ? 'actif' : 'désactivé'}</div>
                  </div>
                  <div>
                    <button className="secondaire" style={{ padding: '6px 10px', fontSize: '0.85rem' }} onClick={() => reinitialiserMotDePasse(csb.id, u)}>
                      Nouveau mot de passe
                    </button>
                    <button className={u.actif ? 'danger' : ''} style={{ padding: '6px 10px', fontSize: '0.85rem' }} onClick={() => toggleUtilisateurActif(csb.id, u)}>
                      {u.actif ? 'Désactiver' : 'Activer'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
